import React, { useCallback, useEffect, useMemo } from 'react';

interface AppLayoutEventsProps {
  onSaveChanges: () => void;
  onCancelChanges: () => void;
  dependencies: any[];
  isLoading: boolean;
  pageLayoutConfig: any;
  LoadingComponent: React.ComponentType | null;
  NoConfigComponent: React.ComponentType | null;
}

interface AppLayoutEventsReturn {
  shouldRenderEarly: boolean;
  earlyComponent: React.ReactElement | null;
}

export function useAppLayoutEvents({
  onSaveChanges,
  onCancelChanges,
  dependencies,
  isLoading,
  pageLayoutConfig,
  LoadingComponent,
  NoConfigComponent,
}: AppLayoutEventsProps): AppLayoutEventsReturn {
  const handleSaveChanges = useCallback(() => {
    onSaveChanges();
  }, [onSaveChanges, ...dependencies]);

  const handleCancelChanges = useCallback(() => {
    onCancelChanges();
  }, [onCancelChanges, ...dependencies]);

  useEffect(() => {
    const handleSaveEvent = () => handleSaveChanges();
    const handleCancelEvent = () => handleCancelChanges();

    window.addEventListener('saveChanges', handleSaveEvent);
    window.addEventListener('cancelChanges', handleCancelEvent);

    return () => {
      window.removeEventListener('saveChanges', handleSaveEvent);
      window.removeEventListener('cancelChanges', handleCancelEvent);
    };
  }, [handleSaveChanges, handleCancelChanges]);

  // Compute early return synchronously to avoid a render flash
  // where shouldRenderEarly is false but pageLayoutConfig is also null
  const { shouldRenderEarly, earlyComponent } = useMemo(() => {
    if (isLoading && LoadingComponent) {
      return {
        shouldRenderEarly: true,
        earlyComponent: React.createElement(LoadingComponent),
      };
    } else if (!isLoading && !pageLayoutConfig && NoConfigComponent) {
      return {
        shouldRenderEarly: true,
        earlyComponent: React.createElement(NoConfigComponent),
      };
    } else if (pageLayoutConfig) {
      return {
        shouldRenderEarly: false,
        earlyComponent: null,
      };
    }
    // Default: still loading, no component yet — show loading if available
    return {
      shouldRenderEarly: !!LoadingComponent,
      earlyComponent: LoadingComponent ? React.createElement(LoadingComponent) : null,
    };
  }, [isLoading, pageLayoutConfig, LoadingComponent, NoConfigComponent]);

  return { shouldRenderEarly, earlyComponent };
}